/**
 * Auswahl-Modal fuer mehrdeutige Entity-Treffer (Customer/Material/Task/Report).
 *
 * Wird vom AssistantDrawer geoeffnet, wenn die SSE-Antwort einen
 * `entity_choice`-Block enthaelt — z.B. zwei Kunden namens "Müller GmbH"
 * mit unterschiedlichen Adressen.
 *
 * Submit ruft generate_from_template erneut auf, mit:
 *   - data_refs[target_ref] = gewaehlte ID
 *   - data_refs[clear_ref] geloescht (sonst nimmt der Server wieder den Namen)
 *
 * Wenn die Re-Submit-Antwort wieder ein selection_required ist (jetzt aber
 * fuer Stunden/Material-Auswahl), wird der Drawer ueber `onContinueSelection`
 * benachrichtigt und uebernimmt — das EntityDisambigModal schliesst sich,
 * der Drawer oeffnet danach das VorlagenSelectionModal.
 */
import { useState } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authFetch, useAuth } from '@onvice/portal-shell-api';

import type { SelectionRequiredEvent } from './AssistantChatPanel';

const ASSISTANT_BASE =
  (import.meta.env.VITE_ASSISTANT_URL as string | undefined)?.replace(/\/+$/, '') || '/api/modules/assistant';

interface EntityOption {
  id: string;
  label: string;
  details: Record<string, string>;
}

export interface EntityChoice {
  entity_type: 'customer' | 'material' | 'task' | 'report';
  target_ref: string;
  clear_ref?: string;
  query: string;
  options: EntityOption[];
  display_fields: string[];
}

export interface EntityDisambigModalProps {
  templateSlug: string;
  dataRefs: Record<string, unknown>;
  entityChoice: EntityChoice;
  onClose: () => void;
  /** Bei Erfolg (Draft erzeugt) — Drawer macht Drafts-Refresh. */
  onCreated?: () => void;
  /** Wenn der Re-Submit ein weiteres SelectionRequired zurueckgibt
   *  (z.B. Stunden-/Material-Auswahl), gibt das Modal das Event nach oben
   *  weiter — Drawer schaltet auf das VorlagenSelectionModal um. */
  onContinueSelection?: (ev: SelectionRequiredEvent) => void;
}

const ENTITY_LABELS_PLURAL: Record<EntityChoice['entity_type'], string> = {
  customer: 'Kunden',
  material: 'Materialien',
  task: 'Aufgaben',
  report: 'Berichte',
};

const FIELD_LABELS: Record<string, string> = {
  // Customer
  address: 'Adresse',
  phone: 'Telefon',
  email: 'E-Mail',
  // Material (Stufe 2, dormant)
  unit: 'Einheit',
  unit_price: 'EP',
  sku: 'Artikel-Nr.',
  category: 'Kategorie',
  default_price: 'Standardpreis',
  // Task
  customer: 'Kunde',
  end_date: 'Fällig',
  status: 'Status',
  // Report
  report_date: 'Datum',
  // Allgemein
  datum: 'Datum',
  mitarbeiter: 'Mitarbeiter',
  aufgabe: 'Aufgabe',
  kunde: 'Kunde',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

export default function EntityDisambigModal({
  templateSlug,
  dataRefs,
  entityChoice,
  onClose,
  onCreated,
  onContinueSelection,
}: EntityDisambigModalProps) {
  const { user } = useAuth();
  const effectiveUserId = user?.id ?? '';
  const effectiveEmail = user?.email ?? '';
  const effectiveRole = user?.role ?? 'default';
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<string>(entityChoice.options[0]?.id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      // data_refs aktualisieren: gewaehlte ID setzen, Namens-Feld loeschen.
      const newRefs: Record<string, unknown> = { ...dataRefs };
      newRefs[entityChoice.target_ref] = selectedId;
      if (entityChoice.clear_ref) delete newRefs[entityChoice.clear_ref];

      const r = await authFetch(`${ASSISTANT_BASE}/assistant/templates/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Portal-User-Id': effectiveUserId,
          'X-Portal-User-Email': effectiveEmail,
          'X-Portal-User-Role': effectiveRole,
        },
        body: JSON.stringify({
          slug: templateSlug,
          data_refs: newRefs,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`);
      }
      const body = (await r.json()) as {
        selection_required?: boolean;
        template_slug?: string;
        data_refs?: Record<string, unknown>;
        sources?: SelectionRequiredEvent['sources'];
        entity_choice?: SelectionRequiredEvent['entity_choice'];
        draft?: { id: string };
        download_url?: string;
      };
      if (body.selection_required && body.sources && body.template_slug) {
        // Folge-Auswahl (z.B. Stunden/Material) — Drawer uebernimmt.
        onContinueSelection?.({
          template_slug: body.template_slug,
          data_refs: body.data_refs ?? newRefs,
          sources: body.sources,
          ...(body.entity_choice ? { entity_choice: body.entity_choice } : {}),
        });
        onClose();
        return;
      }
      onCreated?.();
      onClose();
      // Direkt zur Kundenakte navigieren, wenn customer_id bekannt.
      // KR-5 (Modulanalyse 2026-09-11): /m/stammdaten/kunden ist nur noch ein
      // Redirect, der Query-Parameter VERWIRFT — und openCustomer war eine
      // stammdaten.customers-ID, die das Adressbuch (Contact-Raum) nie
      // aufloesen koennte. /kunden/:id (CustomerDetailPage) macht die
      // Bridge customers-ID -> Contact-ID und reicht ?tab durch.
      const cid = newRefs['customer_id'];
      if (typeof cid === 'string' && cid.length > 0) {
        navigate(`/kunden/${encodeURIComponent(cid)}?tab=akte`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const entityLabelPlural = ENTITY_LABELS_PLURAL[entityChoice.entity_type];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-background border rounded shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <div className="font-semibold">Mehrere {entityLabelPlural} gefunden</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Suchbegriff „{entityChoice.query}" — bitte den richtigen Eintrag waehlen.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded" aria-label="Schliessen">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {entityChoice.options.map((opt) => {
            const checked = opt.id === selectedId;
            return (
              <label
                key={opt.id}
                className={`flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-muted/40 ${
                  checked ? 'border-blue-600 bg-blue-50/40 dark:bg-blue-950/20' : ''
                }`}
              >
                <input
                  type="radio"
                  name="entity-choice"
                  value={opt.id}
                  checked={checked}
                  onChange={() => setSelectedId(opt.id)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{opt.label}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm text-muted-foreground mt-1">
                    {entityChoice.display_fields.map((f) => {
                      const v = opt.details[f];
                      if (!v) return null;
                      return (
                        <>
                          <div key={`l-${f}`} className="text-xs uppercase tracking-wide">{fieldLabel(f)}</div>
                          <div key={`v-${f}`}>{v}</div>
                        </>
                      );
                    })}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="border-t px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-red-600 flex-1 min-w-0 truncate">{error ?? ''}</div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-sm px-3 py-1.5 rounded hover:bg-muted"
            >
              Abbrechen
            </button>
            <button
              onClick={() => void submit()}
              disabled={submitting || !selectedId}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
